/**
 * Prompt-Qualitätsanalyse
 *
 * Punktebasierte Heuristik, um schwache Prompts (reine Stichwörter) zu erkennen
 * und Nutzer auf bessere Formulierungen hinzuweisen.
 */

// Deutsche Fragewörter – ASCII-only, daher \b sicher
const QUESTION_WORDS = /\b(was|wer|wie|wo|welche|welcher|welches|welchem|welchen|warum|wieso|weshalb|wann|wohin|woher|wofür|wobei|wonach|worüber|wozu|worum|wovon|womit)\b/i;

// Deutsche Verben/Imperative – einige enthalten Umlaute (ä, ö, ü)
// Verwende (?:^|\s|[.,!?]) statt \b für Unicode-Kompatibilität
const VERB_PATTERN = new RegExp(
  '(?:^|\\s|[.,!?])(' +
    'nenne|nenn|empfehle|empfiehl|vergleiche|vergleich|liste|list|finde|find|' +
    'zeige|zeig|beschreibe|beschreib|suche|such|gib|gebe|' +
    'erkläre|erklär|zähle|zähl|analysiere|analysier|bewerte|bewert|' +
    'erstelle|erstell|generiere|generier|schlage|schlag|' +
    'identifiziere|identifizier|recherchiere|recherchier' +
  ')(?:\\s|[.,!?]|$)',
  'iu'
);

// Deklarative Sätze: Pronomen + konjugiertes Verb → kein Stichwort-Prompt
const DECLARATIVE_PATTERN = new RegExp(
  '\\b(ich|wir|man)\\s+(' +
    'möchte|möchten|brauche|brauchen|suche|suchen|benötige|benötigen|' +
    'interessiere|interessieren|wohne|wohnen|habe|haben|' +
    'will|wollen|würde|würden|kann|können|muss|müssen|soll|sollen' +
  ')\\b',
  'iu'
);

// Platzhalter-Pattern: [Wort] oder [Wort Wort]
const PLACEHOLDER_PATTERN = /\[[^\]]+\]/g;

/**
 * Analysiert die Qualität eines Prompts und gibt ggf. einen Warnungstext zurück.
 *
 * @returns Warnungstext (deutsch) oder null wenn der Prompt OK ist
 */
export function analyzePromptQuality(text: string): string | null {
  if (!text.trim()) return null;

  // Platzhalter entfernen für Wort-Zählung
  const withoutPlaceholders = text.replace(PLACEHOLDER_PATTERN, '').trim();
  const words = withoutPlaceholders.split(/\s+/).filter((w) => w.length > 0);
  const wordCount = words.length;

  // Ab 4 echten Wörtern keine Warnung
  if (wordCount >= 4) return null;

  let score = 0;

  // Fragewort: +2
  if (QUESTION_WORDS.test(text)) {
    score += 2;
  }

  // Verb/Imperativ: +2
  if (VERB_PATTERN.test(text)) {
    score += 2;
  }

  // Deklarativer Satz (ich möchte, wir brauchen, …): +2
  if (DECLARATIVE_PATTERN.test(text)) {
    score += 2;
  }

  // Endet mit Fragezeichen: +1
  if (text.trimEnd().endsWith('?')) {
    score += 1;
  }

  // Enthält "bitte": +1
  if (/\bbitte\b/i.test(text)) {
    score += 1;
  }

  // Wort-Länge-Malus
  if (wordCount < 3) {
    score -= 3;
  } else if (wordCount <= 4) {
    score -= 1;
  }

  if (score >= 2) return null;

  // Warnungstext je nach Situation
  if (wordCount < 3) {
    return 'Dieser Prompt ist sehr kurz. Formulieren Sie eine vollständige Frage oder Anweisung, damit die KI relevante Ergebnisse liefern kann (z.B. „Welche Unternehmen bieten ... an?").';
  }

  return 'Dieser Prompt sieht aus wie eine Stichwort-Sammlung. Formulieren Sie eine Frage oder Anweisung, damit die KI gezielt Unternehmen auflistet (z.B. „Nenne mir die besten ... in [Ort]").';
}
